import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/shared/hooks/use-auth";
import ProtectedRoute from "@/shared/components/ProtectedRoute";

// ── Dashboard module ──
const Login = lazy(() => import("@/modules/dashboard/pages/Login"));
const Register = lazy(() => import("@/modules/dashboard/pages/Register"));
const Dashboard = lazy(() => import("@/modules/dashboard/pages/Dashboard"));
const Onboard = lazy(() => import("@/modules/dashboard/pages/Onboard"));
const AdminPanel = lazy(() => import("@/modules/dashboard/pages/AdminPanel"));

// ── Staff app module ──
const StaffLogin = lazy(() => import("@/modules/staff-app/pages/StaffLogin"));
const StaffApp = lazy(() => import("@/modules/staff-app/pages/StaffApp"));
const ResetPassword = lazy(() => import("@/modules/staff-app/pages/ResetPassword"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 1000 * 60 * 2, retry: 1 },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-sm text-muted-foreground">Loading…</span>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Auth */}
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboard" element={<Onboard />} />

              {/* Hotel admin dashboard */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["hotel_admin", "hotel_staff"]}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Super admin panel */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["super_admin"]}>
                    <AdminPanel />
                  </ProtectedRoute>
                }
              />

              {/* Staff mobile app */}
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route
                path="/staff"
                element={
                  <ProtectedRoute allowedRoles={["hotel_admin", "hotel_staff"]} redirectTo="/staff/login">
                    <StaffApp />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Login />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
